/**
 * B521 (RULED 2026-09-23, Pathik) — product photos on the tenant ASSET HOST.
 *
 * One grammar for every wall that must agree:
 *  - the upload door mints `tenants/<t>/products/<p>/<photoId>.<ext>` keys and
 *    signs a 5-minute PUT to the ORIGINAL's object key (`src/<key>`);
 *  - surfaces ask for `<asset-host>/i/<key>?w=<width>&fm=<format>`;
 *  - the CloudFront viewer function rewrites that to the VARIANT's object key
 *    (`v/<w>/<fm>/<key>`) — widths from a CLOSED list, so a URL cannot be used
 *    to burn compute; anything else is a 404 at the edge;
 *  - on a miss the resize Lambda parses the same variant path, reads the
 *    original, writes the variant back (each size computed once, cached
 *    forever). The original prefix is never reachable through the variant
 *    grammar, so the original is never served.
 *
 * Ingest keeps ONE guard (the ruling: "resize on upload if its too large"):
 * an original over 4000px on an edge is downscaled once; a file that is not a
 * JPEG/PNG/WebP photo, or over the 5 MB cap, is refused.
 */

export const ASSET_WIDTHS = [160, 320, 480, 800, 1200, 1600] as const;
export type AssetWidth = (typeof ASSET_WIDTHS)[number];
export const ASSET_FORMATS = ["webp", "jpeg"] as const;
export type AssetFormat = (typeof ASSET_FORMATS)[number];
export const DEFAULT_ASSET_FORMAT: AssetFormat = "webp";

export const MAX_INGEST_EDGE = 4000;
export const MAX_PHOTO_UPLOAD_BYTES = 5 * 1024 * 1024;
/** Upload-signing lifetime (the ruling's "5-minute presigned upload"). */
export const PHOTO_UPLOAD_TTL_MS = 5 * 60 * 1000;

export const PHOTO_CONTENT_TYPES = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
} as const;
export type PhotoContentType = keyof typeof PHOTO_CONTENT_TYPES;

/** A photo the door or the ingest guard refuses — the message is operator-facing. */
export class ProductPhotoRejectedError extends Error {
  constructor(why: string) {
    super(why);
    this.name = "ProductPhotoRejectedError";
  }
}

/** Asset storage not configured on this stage — a named gap, never a half-answer. */
export class ProductPhotoUnavailableError extends Error {
  constructor(why: string) {
    super(`Photo uploads are not available on this stage: ${why}`);
    this.name = "ProductPhotoUnavailableError";
  }
}

const SEGMENT = "[A-Za-z0-9][A-Za-z0-9._-]{0,127}";
const DEMO_SLUG = "[a-z0-9][a-z0-9-]{0,62}";
const FILE = "[A-Za-z0-9][A-Za-z0-9-]{0,63}\\.(?:jpg|png|webp)";
/** The key grammar's source — the CloudFront viewer function compiles the same pattern. */
export const ASSET_KEY_PATTERN = `^(?:tenants/${SEGMENT}|demo/${DEMO_SLUG})/products/${SEGMENT}/${FILE}$`;
const KEY_RE = new RegExp(ASSET_KEY_PATTERN);
const SEGMENT_RE = new RegExp(`^${SEGMENT}$`);

/** True only for a key the asset grammar mints — no `..`, no prefixes, no sub-paths. */
export function isAssetKey(key: string): boolean {
  return KEY_RE.test(key) && !key.split("/").some((s) => s === ".." || s === "." || s.includes(".."));
}

export function productPhotoKey(input: {
  readonly tenantId: string;
  readonly productId: string;
  readonly photoId: string;
  readonly contentType: PhotoContentType;
}): string {
  const ext = PHOTO_CONTENT_TYPES[input.contentType];
  const key = `tenants/${input.tenantId}/products/${input.productId}/${input.photoId}.${ext}`;
  if (ext === undefined || !SEGMENT_RE.test(input.tenantId) || !SEGMENT_RE.test(input.productId) || !isAssetKey(key)) {
    throw new ProductPhotoRejectedError("that product cannot hold a photo");
  }
  return key;
}

/** The key sits under THIS tenant's THIS product — the completion door's scope wall. */
export function assetKeyBelongsTo(key: string, tenantId: string, productId: string): boolean {
  return isAssetKey(key) && key.startsWith(`tenants/${tenantId}/products/${productId}/`);
}

export const originalObjectKey = (key: string): string => `src/${key}`;
export const variantObjectKey = (key: string, width: AssetWidth, format: AssetFormat): string =>
  `v/${width}/${format}/${key}`;

export function isAssetWidth(n: number): n is AssetWidth {
  return (ASSET_WIDTHS as readonly number[]).includes(n);
}
export function isAssetFormat(s: string): s is AssetFormat {
  return (ASSET_FORMATS as readonly string[]).includes(s);
}

/** `/v/<w>/<fm>/<key>` → its parts, or null for anything the grammar did not mint. */
export function parseVariantPath(
  path: string,
): { readonly width: AssetWidth; readonly format: AssetFormat; readonly key: string } | null {
  const m = /^\/v\/(\d{1,4})\/([a-z]{3,4})\/(.+)$/.exec(path);
  if (m === null) return null;
  const width = Number(m[1]);
  const format = m[2]!;
  const key = m[3]!;
  if (!isAssetWidth(width) || !isAssetFormat(format) || !isAssetKey(key)) return null;
  return { width, format, key };
}

/**
 * A surface's sized URL: an asset-host photo (`…/i/<key>`) gains
 * `?w=<allowed width ≥ wanted>&fm=webp`; any other https URL is returned
 * untouched (the grammar never rewrites a host it does not own).
 */
export function sizedPhotoUrl(url: string, wantedWidth: number, format: AssetFormat = DEFAULT_ASSET_FORMAT): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (parsed.search !== "" || !parsed.pathname.startsWith("/i/") || !isAssetKey(parsed.pathname.slice(3))) return url;
  const width = ASSET_WIDTHS.find((w) => w >= wantedWidth) ?? ASSET_WIDTHS[ASSET_WIDTHS.length - 1]!;
  return `${url}?w=${width}&fm=${format}`;
}

/** The public URL of a stored photo on the asset host. */
export function assetPhotoUrl(assetHost: string, key: string): string {
  return `https://${assetHost.replace(/^https:\/\//, "").replace(/\/+$/, "")}/i/${key}`;
}

export type IngestPlan =
  | { readonly action: "keep" }
  | { readonly action: "downscale"; readonly maxEdge: number }
  | { readonly action: "refuse"; readonly why: string };

/** The ingest guard's decision over the original's measured metadata. */
export function ingestPlan(meta: {
  readonly format: string;
  readonly width: number;
  readonly height: number;
  readonly bytes: number;
}): IngestPlan {
  if (!["jpeg", "png", "webp"].includes(meta.format)) {
    return { action: "refuse", why: "the file is not a JPEG, PNG or WebP photo" };
  }
  if (meta.bytes > MAX_PHOTO_UPLOAD_BYTES) return { action: "refuse", why: "the photo is larger than 5 MB" };
  if (!(meta.width > 0 && meta.height > 0)) return { action: "refuse", why: "the photo has no readable size" };
  if (Math.max(meta.width, meta.height) > MAX_INGEST_EDGE) return { action: "downscale", maxEdge: MAX_INGEST_EDGE };
  return { action: "keep" };
}

/* ------------------------------------------------------------- the port */

export interface PhotoUploadTicket {
  /** The presigned PUT. */
  readonly url: string;
  /** Headers the PUT MUST carry — they are signed (type + exact length). */
  readonly headers: Readonly<Record<string, string>>;
  readonly expiresAt: number;
}

export interface IngestedPhoto {
  readonly width: number;
  readonly height: number;
  readonly format: "jpeg" | "png" | "webp";
  readonly downscaled: boolean;
}

/** The asset host as the upload + completion doors see it. */
export interface ProductPhotoStore {
  /** Asset host domain (no scheme) the stored photo is served from. */
  readonly assetHost: string;
  presignUpload(key: string, contentType: PhotoContentType, bytes: number, ttlMs: number): Promise<PhotoUploadTicket>;
  /** Measure the uploaded original and apply the ingest guard; refusal deletes it and throws. */
  ingest(key: string): Promise<IngestedPhoto>;
}

/** In-memory reference store (tests + local wiring): holds declared uploads. */
export class InMemoryProductPhotoStore implements ProductPhotoStore {
  readonly assetHost: string;
  private readonly uploads = new Map<string, { contentType: PhotoContentType; bytes: number }>();
  private readonly meta = new Map<string, { format: string; width: number; height: number; bytes: number }>();

  constructor(assetHost = "assets.invalid") {
    this.assetHost = assetHost;
  }

  presignUpload(key: string, contentType: PhotoContentType, bytes: number, ttlMs: number): Promise<PhotoUploadTicket> {
    this.uploads.set(key, { contentType, bytes });
    return Promise.resolve({
      url: `https://${this.assetHost}/upload/${key}`,
      headers: { "content-type": contentType, "content-length": String(bytes) },
      expiresAt: ttlMs,
    });
  }

  /** Test seam: what the browser actually PUT. */
  landed(key: string, meta: { format: string; width: number; height: number; bytes: number }): void {
    this.meta.set(key, meta);
  }

  ingest(key: string): Promise<IngestedPhoto> {
    const m = this.meta.get(key);
    if (m === undefined) return Promise.reject(new ProductPhotoRejectedError("the upload has not arrived yet"));
    const plan = ingestPlan(m);
    if (plan.action === "refuse") {
      this.meta.delete(key);
      return Promise.reject(new ProductPhotoRejectedError(plan.why));
    }
    const scale = plan.action === "downscale" ? plan.maxEdge / Math.max(m.width, m.height) : 1;
    return Promise.resolve({
      width: Math.round(m.width * scale),
      height: Math.round(m.height * scale),
      format: m.format as IngestedPhoto["format"],
      downscaled: plan.action === "downscale",
    });
  }
}

/* ─── B521 open leg (2026-09-23): the demo worker's copy target ─────────
 * The worker copies a prospect's photos under the DEMO half of the one key
 * grammar; the wipe's fence list is DERIVED from the closed width/format
 * lists, so a new width joins the wipe by construction rather than memory. */

const DEMO_SLUG_RE = new RegExp(`^${DEMO_SLUG}$`);

/** The worker's minted key for a copied demo photo — same grammar, demo half. */
export function demoPhotoKey(input: {
  readonly slug: string;
  readonly productId: string;
  /** 1-based position — the file name is `<index>.<ext>`. */
  readonly index: number;
  readonly contentType: PhotoContentType;
}): string {
  const ext = PHOTO_CONTENT_TYPES[input.contentType];
  const key = `demo/${input.slug}/products/${input.productId}/${input.index}.${ext}`;
  if (
    ext === undefined ||
    !Number.isInteger(input.index) ||
    input.index < 1 ||
    !DEMO_SLUG_RE.test(input.slug) ||
    !SEGMENT_RE.test(input.productId) ||
    !isAssetKey(key)
  ) {
    throw new ProductPhotoRejectedError("that demo product cannot hold a photo");
  }
  return key;
}

/**
 * Every object prefix a demo's copied photos can live under — the original
 * plus one per (width, format). The wipe deletes these and NOTHING wider.
 */
export function demoAssetObjectPrefixes(slug: string): readonly string[] {
  if (!DEMO_SLUG_RE.test(slug)) throw new ProductPhotoRejectedError("not a demo slug");
  const under = `demo/${slug}/`;
  return [
    `src/${under}`,
    ...ASSET_WIDTHS.flatMap((w) => ASSET_FORMATS.map((f) => `v/${w}/${f}/${under}`)),
  ];
}
